import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import {
  Autocomplete,
  Chip,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPeopleGroup } from '@fortawesome/pro-light-svg-icons';
import {
  BbClient,
  SiTeamMember,
  assignSiManager,
  getBbClientManagement,
  getSiTeamMembers,
  removeSiManager,
} from './api';

const PageWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin-bottom: ${SPACERS.medium};
  .header-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: ${COLORS.ctaPrimaryBackground};
    color: ${COLORS.ctaPrimary};
  }
  .heading {
    margin: 0;
  }
  .subheading {
    margin: 2px 0 0;
    color: ${COLORS.grayDark};
  }
`;

const StyledTableContainer = styled(TableContainer)`
  border: 1px solid #cfd9e4;
  border-radius: 12px;
  background: ${COLORS.whiteBase};
  td,
  th {
    font-family: inherit;
    font-size: 14px;
  }
  th {
    font-weight: 700;
    background: ${COLORS.greyLightest};
    color: ${COLORS.grayDark};
  }
`;

const ErrorBanner = styled.div`
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  border: 1px solid ${COLORS.critical};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  margin-bottom: ${SPACERS.small};
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.medium};
`;

const AssignRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BbClientManagement: React.FC = () => {
  const client = useClient()!;

  const [clients, setClients] = useState<BbClient[]>([]);
  const [siMembers, setSiMembers] = useState<SiTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAssign, setPendingAssign] = useState<Record<number, SiTeamMember | null>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([getBbClientManagement(client), getSiTeamMembers(client)])
      .then(([clientsRes, membersRes]) => {
        setClients(clientsRes.data.bbClientManagement);
        setSiMembers(membersRes.data.siTeamMembers);
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssign = async (accountId: number) => {
    const member = pendingAssign[accountId];
    if (!member) return;
    setSaving(prev => ({ ...prev, [accountId]: true }));
    try {
      const res = await assignSiManager(client, accountId, member.userId);
      if (!res.data?.assignSiManagerToClient.success) {
        setError(res.data?.assignSiManagerToClient.error ?? 'Failed to assign');
      } else {
        setError(null);
        setPendingAssign(prev => ({ ...prev, [accountId]: null }));
        loadData();
      }
    } catch {
      setError('Failed to assign SI manager');
    } finally {
      setSaving(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const handleRemove = async (accountId: number, userId: number) => {
    setSaving(prev => ({ ...prev, [accountId]: true }));
    try {
      const res = await removeSiManager(client, accountId, userId);
      if (!res.data?.removeSiManagerFromClient.success) {
        setError(res.data?.removeSiManagerFromClient.error ?? 'Failed to remove');
      } else {
        setError(null);
        loadData();
      }
    } catch {
      setError('Failed to remove SI manager');
    } finally {
      setSaving(prev => ({ ...prev, [accountId]: false }));
    }
  };

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-icon">
          <FontAwesomeIcon icon={faPeopleGroup} size="lg" />
        </div>
        <div>
          <TrueImpactText variant="heading-page" className="heading">
            Client Management
          </TrueImpactText>
          <TrueImpactText className="subheading">
            Assign SI team members and/or NPO Specialists to clients.
          </TrueImpactText>
        </div>
      </PageHeader>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {loading ? (
        <LoadingWrapper>
          <TrueImpactLoading />
        </LoadingWrapper>
      ) : (
        <StyledTableContainer>
          <MuiTable>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Account ID</TableCell>
                <TableCell>Assigned SI Managers</TableCell>
                <TableCell style={{ minWidth: 300 }}>Add SI Manager</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map(c => {
                const assignableMembers = siMembers.filter(
                  m => !c.siManagers.some(assigned => assigned.userId === m.userId),
                );
                return (
                  <TableRow key={c.accountId}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell style={{ color: '#888', fontFamily: 'monospace' }}>
                      #{c.accountId}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {c.siManagers.length === 0 ? (
                          <span style={{ color: COLORS.grayDark, fontSize: 13 }}>
                            None
                          </span>
                        ) : (
                          c.siManagers.map(m => (
                            <Chip
                              key={m.userId}
                              label={m.fullName}
                              size="small"
                              onDelete={
                                saving[c.accountId]
                                  ? undefined
                                  : () => handleRemove(c.accountId, m.userId)
                              }
                              sx={{ fontSize: 12 }}
                            />
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <AssignRow>
                        <Autocomplete
                          options={assignableMembers}
                          getOptionLabel={m => `${m.fullName} (${m.email})`}
                          value={pendingAssign[c.accountId] ?? null}
                          onChange={(_e, val) =>
                            setPendingAssign(prev => ({ ...prev, [c.accountId]: val }))
                          }
                          isOptionEqualToValue={(a, b) => a.userId === b.userId}
                          size="small"
                          style={{ minWidth: 220 }}
                          componentsProps={{ paper: { sx: { fontSize: 13 } } }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder="Select member"
                              inputProps={{
                                ...params.inputProps,
                                style: { fontSize: 13 },
                              }}
                            />
                          )}
                        />
                        <Tooltip
                          title={!pendingAssign[c.accountId] ? 'Select a member first' : ''}
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!pendingAssign[c.accountId] || saving[c.accountId]}
                              onClick={() => handleAssign(c.accountId)}
                              sx={{
                                background: COLORS.ctaPrimaryBackground,
                                color: COLORS.ctaPrimary,
                                borderRadius: 2,
                                '&:hover': {
                                  background: COLORS.ctaPrimarySubdued,
                                },
                                '&:disabled': { opacity: 0.4 },
                              }}
                            >
                              <FontAwesomeIcon icon={faPeopleGroup} style={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </AssignRow>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </MuiTable>
        </StyledTableContainer>
      )}
    </PageWrapper>
  );
};

export default BbClientManagement;
